import { Editor, Range, Point, Element, Transforms, Path } from 'slate';

/**
 * Plugin for table editing: guards against cell deletion, handles multi-cell selection
 * @param {Editor} editor - Slate editor instance
 * @returns {Editor} Extended editor with table guards
 */
const withTable = (editor) => {
    const { deleteBackward, deleteForward, insertData, deleteFragment } = editor;

    // List handling (Enter, Backspace in lists) is in withLists plugin

    // Helper function to clear content of selected rows
    const clearSelectedRows = () => {
        if (!editor.selectedRows?.length) return false;

        editor.selectedRows.forEach(({ tablePath, rowIndex }) => {
            const [table] = Editor.node(editor, tablePath);
            const row = table.children[rowIndex];
            if (row && row.children) {
                row.children.forEach((cell, cellIndex) => {
                    const cellPath = [...tablePath, rowIndex, cellIndex, 0, 0];
                    try {
                        Transforms.delete(editor, { at: cellPath });
                        Transforms.insertText(editor, '', { at: [...tablePath, rowIndex, cellIndex, 0, 0] });
                    } catch (e) {
                        console.warn('Could not clear cell:', e);
                    }
                });
            }
        });
        return true;
    };

    // Helper function to clear content of selected columns
    const clearSelectedColumns = () => {
        if (!editor.selectedColumns?.length) return false;

        editor.selectedColumns.forEach(({ tablePath, columnIndex }) => {
            const [table] = Editor.node(editor, tablePath);
            table.children.forEach((row, rowIndex) => {
                const cellPath = [...tablePath, rowIndex, columnIndex, 0, 0];
                try {
                    Transforms.delete(editor, { at: cellPath });
                    Transforms.insertText(editor, '', { at: [...tablePath, rowIndex, columnIndex, 0, 0] });
                } catch (e) {
                    console.warn('Could not clear cell:', e);
                }
            });
        });
        return true;
    };

    // Helper function to clear content of multiple selected cells
    const clearMultipleCells = (selection) => {
        if (!selection || Range.isCollapsed(selection)) return false;

        // Get all cells in the selection
        const cells = Array.from(
            Editor.nodes(editor, {
                at: selection,
                match: (n) => !Editor.isEditor(n) && Element.isElement(n) && (n.type === 'table-cell' || n.type === 'table-header'),
            }),
        );

        // If selection spans multiple cells, manually clear content of each cell
        if (cells.length > 1) {
            Editor.withoutNormalizing(editor, () => {
                cells.forEach(([cell, cellPath]) => {
                    // Get the paragraph inside the cell (usually at index 0)
                    const paragraphPath = [...cellPath, 0];

                    try {
                        // Remove all children of the paragraph
                        const [paragraph] = Editor.node(editor, paragraphPath);
                        if (paragraph && paragraph.children) {
                            // Delete all text nodes in the paragraph
                            for (let i = paragraph.children.length - 1; i >= 0; i--) {
                                Transforms.delete(editor, { at: [...paragraphPath, i] });
                            }
                            // Insert empty text node
                            Transforms.insertNodes(editor, { text: '' }, { at: [...paragraphPath, 0] });
                        }
                    } catch (e) {
                        console.warn('Could not clear cell at', cellPath, e);
                    }
                });
            });
            return true;
        }

        return false;
    };

    // ✅ FIX: Normalize pasted tables to have first row as headers
    editor.insertData = (data) => {
        const html = data.getData('text/html');

        // Call original insertData
        insertData(data);

        // After paste, normalize any newly inserted tables
        if (!html || !html.includes('<table')) {
            return;
        }

        setTimeout(() => {
            const { selection } = editor;
            if (!selection) return;

            // Find all tables in the document
            const tables = Array.from(
                Editor.nodes(editor, {
                    at: [],
                    match: (n) => !Editor.isEditor(n) && Element.isElement(n) && n.type === 'table',
                }),
            );

            // Normalize each table
            tables.forEach(([table, tablePath]) => {
                if (!table.children || table.children.length === 0) return;

                const firstRow = table.children[0];
                if (!firstRow || !firstRow.children) return;

                // Check if first row has any non-header cells
                const hasNonHeaderCells = firstRow.children.some((cell) => cell.type === 'table-cell');

                if (hasNonHeaderCells) {
                    // Convert all cells in first row to table-header
                    firstRow.children.forEach((cell, cellIndex) => {
                        if (cell.type === 'table-cell') {
                            const cellPath = [...tablePath, 0, cellIndex];
                            Transforms.setNodes(editor, { type: 'table-header' }, { at: cellPath });
                        }
                    });
                }
            });
        }, 0);
    };

    // ✅ FIX: Override deleteFragment to prevent cell deletion when selection spans multiple cells
    // This is the method Slate calls when there's a selection and you press Delete/Backspace
    editor.deleteFragment = (direction) => {
        const { selection } = editor;

        if (!selection) {
            deleteFragment(direction);
            return;
        }

        // Get all cells in the selection
        const cells = Array.from(
            Editor.nodes(editor, {
                at: selection,
                match: (n) => !Editor.isEditor(n) && Element.isElement(n) && (n.type === 'table-cell' || n.type === 'table-header'),
            }),
        );

        // If selection spans multiple cells, manually clear their content
        if (cells.length > 1) {
            Editor.withoutNormalizing(editor, () => {
                cells.forEach(([cell, cellPath]) => {
                    // Get the paragraph inside the cell (usually at index 0)
                    const paragraphPath = [...cellPath, 0];

                    try {
                        // Remove all children of the paragraph and replace with empty text
                        const [paragraph] = Editor.node(editor, paragraphPath);
                        if (paragraph && paragraph.children) {
                            // Delete all text nodes in the paragraph
                            for (let i = paragraph.children.length - 1; i >= 0; i--) {
                                Transforms.removeNodes(editor, { at: [...paragraphPath, i] });
                            }
                            // Insert empty text node
                            Transforms.insertNodes(editor, { text: '' }, { at: [...paragraphPath, 0] });
                        }
                    } catch (e) {
                        console.warn('Could not clear cell at', cellPath, e);
                    }
                });
            });

            // Move cursor to first cell
            if (cells.length > 0) {
                const [, firstCellPath] = cells[0];
                const point = Editor.start(editor, firstCellPath);
                Transforms.select(editor, point);
            }
            return;
        }

        // If only one cell or no cells, use default behavior
        deleteFragment(direction);
    };

    editor.deleteBackward = (unit) => {
        const { selection } = editor;

        // ✅ FIX: If rows or columns are selected, clear their content instead of deleting cells
        if (clearSelectedRows()) return;
        if (clearSelectedColumns()) return;

        // ✅ FIX: Check if selection spans multiple table cells (text selection, not row/column selector)
        if (clearMultipleCells(selection)) return;

        if (selection && Range.isCollapsed(selection)) {
            // List backspace is handled by withLists plugin

            // Handle table cells
            const [cell] = Editor.nodes(editor, {
                match: (n) => !Editor.isEditor(n) && Element.isElement(n) && n.type === 'table-cell',
            });
            const prevNodePath = Editor.before(editor, selection);

            const [tableNode] = Editor.nodes(editor, {
                at: prevNodePath,
                match: (n) => !Editor.isEditor(n) && Element.isElement(n) && n.type === 'table-cell',
            });

            if (cell) {
                const [, cellPath] = cell;

                const start = Editor.start(editor, cellPath);
                if (Point.equals(selection.anchor, start)) {
                    return;
                }
            }
            if (!cell && tableNode) {
                return;
            }
        }

        deleteBackward(unit);
    };

    editor.deleteForward = (unit) => {
        const { selection } = editor;

        // ✅ FIX: If rows or columns are selected, clear their content instead of deleting cells
        if (clearSelectedRows()) return;
        if (clearSelectedColumns()) return;

        // ✅ FIX: Check if selection spans multiple table cells (text selection, not row/column selector)
        if (clearMultipleCells(selection)) return;

        if (selection && Range.isCollapsed(selection)) {
            const [cell] = Editor.nodes(editor, {
                match: (n) => !Editor.isEditor(n) && Element.isElement(n) && n.type === 'table-cell',
            });

            const prevNodePath = Editor.after(editor, selection);
            const [tableNode] = Editor.nodes(editor, {
                at: prevNodePath,
                match: (n) => !Editor.isEditor(n) && Element.isElement && n.type === 'table-cell',
            });

            if (cell) {
                const [, cellPath] = cell;
                const end = Editor.end(editor, cellPath);

                if (Point.equals(selection.anchor, end)) {
                    return;
                }
            }
            if (!cell && tableNode) {
                return;
            }
        }

        deleteForward(unit);
    };

    return editor;
};

export default withTable;
